import { useEffect, useState } from "react"

import { useMutationAddNewClass } from "api/reactQuery/mutations/classes"
import { useQueryClasses } from "api/reactQuery/queries/classes"
import { useCheckClassesWithStudents } from "hooks/checkClassesWithStudents/useCheckClassesWithStudents"
import { useDialog } from "hooks/dialog"
import { useIsSchoolPlan } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useNavigate } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { getErrorMessageFromTab } from "utils/api"

import { defaultNewClassState } from "./sendTransactionForm/defaultNewClassState"

interface UseQuickActionValidationReturn {
  isLoadingValidation: boolean
  canOpenSendTransaction: boolean
  handleSendTransactionClick: () => boolean
  isAddClassDialogOpen: boolean
  handleCloseAddClassDialog: () => void
  newClassState: typeof defaultNewClassState
  setNewClassState: React.Dispatch<
    React.SetStateAction<typeof defaultNewClassState>
  >
  createNewClass: (data: typeof defaultNewClassState) => void
  isLoadingCreateClass: boolean
}

export const useQuickActionValidation = (): UseQuickActionValidationReturn => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showSnackbar } = useCustomSnackbar()
  const { t } = useTranslation()
  const isSchoolPlan = useIsSchoolPlan()

  const [canOpenSendTransaction, setCanOpenSendTransaction] = useState(false)
  const [isLoadingValidation, setIsLoadingValidation] = useState(true)

  const {
    isOpen: isAddClassDialogOpen,
    handleOpen: handleOpenAddClassDialog,
    handleClose: handleCloseAddClassDialog,
  } = useDialog()

  const [newClassState, setNewClassState] = useState(defaultNewClassState)

  const { data: classesData, isLoading: isLoadingClasses } = useQueryClasses()

  const { isLoadingStudents, hasClassWithStudents } =
    useCheckClassesWithStudents({
      classesData: classesData?.data,
      isLoadingClasses,
    })

  const { mutate: createNewClass, isLoading: isLoadingCreateClass } =
    useMutationAddNewClass({
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries("classes")
          showSnackbar({
            title: t("addNewClass.classCreatedSucceed"),
          })
          handleCloseAddClassDialog()
          setNewClassState(defaultNewClassState)
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessageFromTab(error),
            variant: "error",
          })
        },
      },
    })

  useEffect(() => {
    if (isLoadingClasses || isLoadingStudents) {
      setIsLoadingValidation(true)
      setCanOpenSendTransaction(true)
      return
    }

    setIsLoadingValidation(false)

    if (isSchoolPlan) {
      setCanOpenSendTransaction(true)
      return
    }

    if (!classesData?.data || !classesData.data.length) {
      setCanOpenSendTransaction(false)
      return
    }

    setCanOpenSendTransaction(hasClassWithStudents)
  }, [isLoadingClasses, isLoadingStudents, classesData, hasClassWithStudents, isSchoolPlan])

  const handleSendTransactionClick = () => {
    if (isSchoolPlan) {
      return true
    }

    if (!canOpenSendTransaction) {
      if (!classesData?.data || !classesData.data.length) {
        handleOpenAddClassDialog()
        return false
      }

      if (!hasClassWithStudents && !isLoadingStudents) {
        const firstClassId = classesData.data[0]?.id
        if (firstClassId) {
          navigate(RoutePath.CLASS.replace(":classId", `${firstClassId}`))
        }
        return false
      }
    }

    return true
  }

  return {
    isLoadingValidation,
    canOpenSendTransaction,
    handleSendTransactionClick,
    isAddClassDialogOpen,
    handleCloseAddClassDialog,
    newClassState,
    setNewClassState,
    createNewClass,
    isLoadingCreateClass,
  }
}
